import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  MouseEvent,
} from 'react';
import { FiDownload } from 'react-icons/fi';
import jsPDF from 'jspdf';
import { validateImageUrl } from 'utils/imageSecurityUtils';
import html2canvas from 'html2canvas-pro';
import {
  subscribeToCompletion,
  subscribeToRequestStatus,
} from 'utils/requestTracker';

import {
  DownloadButton,
  Tooltip,
  MenuContainer,
  MenuItem,
  Wrapper,
} from './styles';
import { color } from 'theme/Color';
import store from '../../store/store';
import { FilterContext } from 'components/FilterBar/FilterProvider';

interface Option {
  id: number;
  label: string;
  action: (event?: React.MouseEvent) => void;
}

interface DownloadMenuProps {
  theme: string;
}

const DownloadMenu: React.FC<DownloadMenuProps> = ({ theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [tooltipText, setTooltipText] = useState(
    'Enabled on complete page load'
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { selectedProduct } = useContext(FilterContext);
  const isMFE = store.getState().app.isMFE;
  const parentElementId = store.getState().app.mfeHostConfig?.parentElementId;

  useEffect(() => {
    // Subscribe to ongoing status updates
    const statusUnsubscribe = subscribeToRequestStatus(
      ({ activeRequestCount }) => {
        if (activeRequestCount === 0) {
          setIsDisabled(false);
        } else {
          setIsDisabled(true);
        }
      }
    );

    // Subscribe to completion events
    const completionUnsubscribe = subscribeToCompletion(() => {
      setIsDisabled(false);
    });

    return () => {
      statusUnsubscribe();
      completionUnsubscribe();
    };
  }, []);

  const resetTooltipText = () => {
    setTooltipText('Enabled on complete page load');
  };

  // Shared helper function for MFE-specific element modifications
  const applyMfeStyleModifications = (doc: Document) => {
    // Apply overrides to week picker, sector select, and market select
    const weekPickerElements = doc.querySelectorAll(
      '#week-picker > div > label > span'
    );
    const sectorSelectElements = doc.querySelectorAll(
      '#sector-select > div > div > div'
    );
    const marketSelectElements = doc.querySelectorAll(
      '#market-select > div > div > div'
    );
    // Apply overrides to KPI badge spans
    const kpiBadgeSpans = doc.querySelectorAll(
      'div[data-kpi-badge="true"] > span'
    );
    const tagContainer = doc.querySelectorAll(
      'div[data-testid="tile-tag-container"]'
    );
    const tagContainerDivIndexVsPy = doc.querySelectorAll(
      'div[data-testid="tag-text-index-vs-py"]'
    );
    const tagContainerDiv = doc.querySelectorAll(
      'div[data-testid="tile-tag-container"] > div > span'
    );

    if (tagContainer && tagContainer.length > 0) {
      tagContainer.forEach((tag: HTMLElement) => {
        tag.style.display = 'flex';
        tag.style.flexDirection = 'row';
        tag.style.webkitBoxAlign = 'center';
        tag.style.alignItems = 'center';
        tag.style.paddingTop = '10px';
      });
      tagContainerDiv.forEach((tag: HTMLElement) => {
        tag.style.paddingBottom = '10px';
      });
    }

    // Add top margin to SVG elements
    const svgElements = doc.querySelectorAll(
      'div[data-testid="tile-tag-container"] svg'
    );
    if (svgElements && svgElements.length > 0) {
      svgElements.forEach((svg: SVGElement) => {
        svg.style.marginTop = '14px';
      });
    }

    const cardHeaderElement = doc.querySelectorAll(
      'h5[data-testid="card-header"]'
    );
    if (cardHeaderElement && cardHeaderElement.length > 0) {
      cardHeaderElement.forEach((h2: HTMLElement) => {
        h2.style.paddingBottom = '15px';
      });
    }

    [
      weekPickerElements,
      sectorSelectElements,
      marketSelectElements,
      kpiBadgeSpans,
      tagContainerDivIndexVsPy,
    ].forEach((elements) => {
      if (elements && elements.length > 0) {
        elements.forEach((element: HTMLElement) => {
          element.style.cssText +=
            'padding-top: 0px !important; padding-bottom: 10px !important;';
        });
      }
    });
  };

  const applySwitcherModifications = (doc: Document) => {
    // Fix Switcher component visibility and styling
    const switcherContainers = doc.querySelectorAll('.switcher');
    if (switcherContainers && switcherContainers.length > 0) {
      switcherContainers.forEach((switcher: HTMLElement) => {
        // Ensure switcher container is visible and properly styled
        switcher.style.display = 'inline-flex';
        switcher.style.visibility = 'visible';
        switcher.style.opacity = '1';
        switcher.style.position = 'relative';
        switcher.style.alignItems = 'center';
        switcher.style.gap = '0px';
        switcher.style.borderRadius = '8px';
        switcher.style.overflow = 'hidden';
        switcher.style.backgroundColor =
          theme === 'light' ? '#F3F7FD' : '#272727';
        switcher.style.border =
          theme === 'light' ? '1px solid #D7E5F8' : '1px solid #3A3A3A';
        switcher.style.padding = '4px';
      });
    }

    // Fix padding-top issue for buttons in switcher element with stronger override
    const switcherButtons = doc.querySelectorAll('.switcher button');
    if (switcherButtons && switcherButtons.length > 0) {
      switcherButtons.forEach((button: HTMLElement) => {
        // Ensure button is visible
        button.style.display = 'inline-block';
        button.style.visibility = 'visible';
        button.style.opacity = '1';

        // Apply core button styles
        button.style.border = 'none';
        button.style.cursor = 'pointer';
        button.style.outline = 'none';
        button.style.transition = 'all 0.2s ease';
        button.style.fontFamily =
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        button.style.fontSize = '14px';
        button.style.lineHeight = '1.4';
        button.style.whiteSpace = 'nowrap';
        button.style.textAlign = 'center';
        button.style.minWidth = 'fit-content';

        // Apply padding
        button.style.paddingTop = '8px';
        button.style.paddingBottom = '8px';
        button.style.paddingLeft = '16px';
        button.style.paddingRight = '16px';

        // Check if button is selected (has aria-selected="true" or class "selected")
        const isSelected =
          button.getAttribute('aria-selected') === 'true' ||
          button.classList.contains('selected');

        if (isSelected) {
          // Selected button styles
          button.style.backgroundColor = '#0069D7';
          button.style.color = '#FFFFFF';
          button.style.fontWeight = '700';
          button.style.borderRadius = '6px';
        } else {
          // Unselected button styles
          button.style.backgroundColor = 'transparent';
          button.style.color = theme === 'light' ? '#616161' : '#EFEFEF';
          button.style.fontWeight = '400';
          button.style.borderRadius = '6px';
        }
      });
    }

    // Fix Week Picker component visibility (minimal fix to preserve layout)
    const weekPickerLabels = doc.querySelectorAll(
      'label[data-testid="date-picker-label"]'
    );
    if (weekPickerLabels && weekPickerLabels.length > 0) {
      weekPickerLabels.forEach((label: HTMLElement) => {
        // Only ensure visibility without disrupting layout
        label.style.visibility = 'visible';
        label.style.opacity = '1';

        // Ensure child elements are visible
        const dateSpan = label.querySelector('span');
        if (dateSpan) {
          (dateSpan as HTMLElement).style.visibility = 'visible';
          (dateSpan as HTMLElement).style.opacity = '1';
        }

        // Ensure icon and SVG are visible
        const iconDiv = label.querySelector('div');
        if (iconDiv) {
          (iconDiv as HTMLElement).style.visibility = 'visible';
          (iconDiv as HTMLElement).style.opacity = '1';

          const svgIcon = iconDiv.querySelector('svg');
          if (svgIcon) {
            (svgIcon as SVGElement).style.visibility = 'visible';
            (svgIcon as SVGElement).style.opacity = '1';
          }
        }
      });
    }

    // Fix "Select Week" label visibility
    const weekPickerTitles = doc.querySelectorAll(
      '.DatePickerContainer > div:first-child'
    );
    if (weekPickerTitles && weekPickerTitles.length > 0) {
      weekPickerTitles.forEach((title: HTMLElement) => {
        title.style.visibility = 'visible';
        title.style.opacity = '1';
      });
    }
  };

  // Helper: Replaces the Date Picker Input with a text span to prevent clipping
  const fixDatePickerInput = (doc: Document) => {
    // Select the specific date picker input using your testid
    const dateInput = doc.querySelector(
      'input[data-testid="date-picker-input"]'
    ) as HTMLInputElement;

    if (dateInput && dateInput.parentElement) {
      const value = dateInput.value;
      const computed = window.getComputedStyle(dateInput);

      // Create a span to replace the input
      const textSpan = doc.createElement('span');
      textSpan.innerText = value;

      // Copy font styles to ensure it looks identical
      textSpan.style.fontFamily = computed.fontFamily;
      textSpan.style.fontSize = computed.fontSize;
      textSpan.style.fontWeight = computed.fontWeight;
      textSpan.style.color = computed.color;
      textSpan.style.letterSpacing = computed.letterSpacing;

      // Fix the layout to prevent clipping
      textSpan.style.display = 'flex';
      textSpan.style.alignItems = 'center';
      textSpan.style.height = '100%';
      // Add a small top padding only if it still feels too high, otherwise flex center handles it
      // textSpan.style.paddingTop = '2px';

      // Preserve original left padding so it doesn't jump to the left
      textSpan.style.paddingLeft = computed.paddingLeft || '8px';

      // Replace the input with the span in the CLONED document
      dateInput.style.display = 'none';
      dateInput.parentElement.insertBefore(textSpan, dateInput);
    }
  };

  // Helper: Fixes the Week Picker Label visibility and alignment
  const fixWeekPickerLabel = (doc: Document) => {
    // 1. Find the stable label element first
    const label = doc.querySelector(
      'label[data-testid="date-picker-label"]'
    ) as HTMLElement;

    if (!label) {
      return;
    }

    // 2. Target the direct parent container (The white box wrapper)
    const pickerContainer = label.parentElement;

    if (pickerContainer) {
      // Force the container to align its items centrally
      pickerContainer.style.display = 'flex';
      pickerContainer.style.alignItems = 'center';
      pickerContainer.style.justifyContent = 'center';
      pickerContainer.style.margin = '0';
      pickerContainer.style.padding = '0';

      // Ensure it sits correctly in the row
      pickerContainer.style.position = 'relative';
      pickerContainer.style.top = '0';
    }

    // 3. Fix the label itself (The clickable area)
    label.style.display = 'flex';
    label.style.alignItems = 'center';
    label.style.opacity = '1';
    label.style.visibility = 'visible';
    label.style.height = '100%';
    label.style.paddingTop = '0';
    label.style.paddingBottom = '0';
    label.style.marginTop = '0';
    label.style.marginBottom = '0';

    // 4. Fix the inner span containing the text date
    const textSpan = label.querySelector('span');
    if (textSpan) {
      const computed = window.getComputedStyle(textSpan);
      textSpan.style.display = 'flex';
      textSpan.style.alignItems = 'center';
      textSpan.style.height = '100%';
      textSpan.style.opacity = '1';
      textSpan.style.visibility = 'visible';
      textSpan.style.color = computed.color;
      textSpan.style.whiteSpace = 'nowrap';
      // Reset line-height to normal to prevent vertical offsets
      textSpan.style.lineHeight = 'normal';
    }

    // 5. Fix the icon container (It is the direct DIV inside the label)
    const iconDiv = label.querySelector(':scope > div') as HTMLElement;
    if (iconDiv) {
      iconDiv.style.display = 'flex';
      iconDiv.style.alignItems = 'center';
      iconDiv.style.height = '100%';
      iconDiv.style.marginTop = '0';
    }
  };

  const modifyPdfElements = (clonedDoc: Document) => {
    const titleElement = clonedDoc.getElementById('program-title');
    const productTabTitle = clonedDoc.getElementById('pdf-product-title');
    const filterBar = clonedDoc.getElementById('filters');
    const yearFilter = clonedDoc.getElementById('yearValue');
    const pepsiLogo = clonedDoc.querySelector<HTMLElement>('.pepsicoIcon');

    if (pepsiLogo) {
      pepsiLogo.style.margin = '0';
    }
    if (titleElement && productTabTitle) {
      titleElement.style.display = 'none';
      productTabTitle.style.display = 'block';
    }
    if (filterBar && yearFilter) {
      filterBar.style.display = 'none';
      yearFilter.style.display = 'block';
    }
    applySwitcherModifications(clonedDoc);
    fixDatePickerInput(clonedDoc);
    fixWeekPickerLabel(clonedDoc);

    if (isMFE) {
      applyMfeStyleModifications(clonedDoc);
    }
    return clonedDoc;
  };

  const modifyImageElements = (clonedDoc: Document) => {
    const pepsiLogo = clonedDoc.querySelector<HTMLElement>('.pepsicoIcon');
    if (pepsiLogo) {
      pepsiLogo.style.margin = '0';
    }
    applySwitcherModifications(clonedDoc);
    fixDatePickerInput(clonedDoc);
    fixWeekPickerLabel(clonedDoc);

    if (isMFE) {
      applyMfeStyleModifications(clonedDoc);
    }

    return clonedDoc;
  };

  function getTimestampForFilename() {
    const now = new Date();
    // Format: YYYY-MM-DD_HH-MM-SS
    const timestamp =
      now.getFullYear() +
      '-' +
      String(now.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(now.getDate()).padStart(2, '0') +
      '_' +
      String(now.getHours()).padStart(2, '0') +
      '-' +
      String(now.getMinutes()).padStart(2, '0') +
      '-' +
      String(now.getSeconds()).padStart(2, '0');

    return timestamp;
  }

  function generateFileName() {
    if (isMFE) {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0'); // 01-12
      const year = now.getFullYear();

      // Format: <Product> Success Metrics - MMYYYY
      return `${selectedProduct?.productName} Success Metrics - ${month}${year}.pdf`;
    } else {
      // For regular mode
      return `psd-summary-${getTimestampForFilename()}.pdf`;
    }
  }

  const handleExportPDF = async () => {
    setIsOpen(false);
    setIsDisabled(true);
    setTooltipText('Please wait while PDF is being generated');

    const previewContent = isMFE
      ? document.getElementById(parentElementId)
      : document.getElementById('root');

    if (!previewContent) {
      console.error('Preview content not found');
      setIsDisabled(false);
      return;
    }

    // 1. Identify the internal scrollable container (The Tab Content)
    // This is the element that actually has the scrollbar
    const scrollContainer = previewContent.querySelector(
      'div[data-tab-content="true"]'
    ) as HTMLElement;

    // Helper to backup and apply temporary styles
    const originalStyles = new Map<HTMLElement, string>();
    const setTempStyle = (el: HTMLElement, cssText: string) => {
      originalStyles.set(el, el.getAttribute('style') || '');
      el.style.cssText += cssText;
    };

    try {
      // --- PHASE 1: DOM EXPANSION ---
      // Instead of scrolling, we force the container to be "tall" enough to show everything at once.

      if (scrollContainer) {
        // Force the tab content to expand fully
        setTempStyle(
          scrollContainer,
          'height: auto !important; overflow: visible !important; max-height: none !important;'
        );

        // Also expand its immediate child if it exists (often the grid container)
        const childDiv = scrollContainer.querySelector(
          ':scope > div'
        ) as HTMLElement;
        if (childDiv) {
          setTempStyle(
            childDiv,
            'height: auto !important; overflow: visible !important;'
          );
        }
      }

      // Expand all parent containers up to the root.
      // If parents have 'height: 100vh' or 'overflow: hidden', they will clip our expanded child.
      let currentEl = scrollContainer?.parentElement;
      while (currentEl && currentEl !== previewContent.parentElement) {
        if (currentEl instanceof HTMLElement) {
          setTempStyle(
            currentEl,
            'height: auto !important; overflow: visible !important; flex: none !important; display: block !important;'
          );
        }
        currentEl = currentEl.parentElement;
      }

      // Handle specific elements to hide/clean up based on your previous code
      const tabContainer = document.getElementById('tab-container');
      if (tabContainer) {
        tabContainer.setAttribute('data-html2canvas-ignore', '');
      }

      // Wait a moment for the DOM layout to update after expansion
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Recalculate dimensions based on the EXPANDED content
      const totalHeight = previewContent.scrollHeight;
      const actualWidth = previewContent.scrollWidth;

      // --- PHASE 2: CAPTURE ---
      const canvas = await html2canvas(previewContent, {
        useCORS: true,
        scale: window.devicePixelRatio,
        allowTaint: true,
        backgroundColor: theme === 'light' ? color._FBFCFE : color._131313,

        // CRITICAL: We tell html2canvas the window is huge.
        // This ensures it captures the full expanded height without cropping.
        windowWidth: actualWidth,
        windowHeight: totalHeight,
        width: actualWidth,
        height: totalHeight,

        // Offset for MFE vs Standalone
        x: isMFE ? 0 : previewContent.offsetLeft,

        onclone: (clonedDoc) => {
          modifyPdfElements(clonedDoc);

          // Ensure the cloned root/body also permits expansion
          const clonedRoot = isMFE
            ? clonedDoc.getElementById(parentElementId)
            : clonedDoc.getElementById('root');

          if (clonedRoot) {
            clonedRoot.style.height = 'auto';
            clonedRoot.style.overflow = 'visible';
          }
        },
        logging: false,
        removeContainer: true,
      });

      // --- PHASE 3: PDF GENERATION ---
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation: actualWidth > totalHeight ? 'landscape' : 'portrait',
        unit: 'px',
        format: [actualWidth, totalHeight],
      });

      pdf.addImage(imgData, 'PNG', 0, 0, actualWidth, totalHeight);
      pdf.save(generateFileName());
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      // --- PHASE 4: RESTORE ---
      // Restore original DOM styles instantly
      originalStyles.forEach((styleStr, el) => {
        if (styleStr) {
          el.setAttribute('style', styleStr);
        } else {
          el.removeAttribute('style');
        }
      });

      const tabContainer = document.getElementById('tab-container');
      if (tabContainer) {
        tabContainer.removeAttribute('data-html2canvas-ignore');
      }

      setIsDisabled(false);
      resetTooltipText();
    }
  };

  const handleImageCopy = async (event: React.MouseEvent) => {
    event.preventDefault();
    setIsOpen(false);
    setIsDisabled(true);
    setTooltipText('Please wait while image is being processed');

    // 1. Identify the root element to capture
    const previewContent = isMFE
      ? document.getElementById(parentElementId)
      : document.getElementById('root');

    if (!previewContent) {
      return;
    }

    // 2. Identify the specific container holding the Tab Content
    // Based on your DOM, this seems to be the one with the scrollbar
    const scrollContainer = previewContent.querySelector(
      'div[data-tab-content="true"]'
    ) as HTMLElement;

    // Store original styles to restore later
    const originalStyles = new Map<HTMLElement, string>();

    // Helper to backup and modify style
    const setTempStyle = (el: HTMLElement, cssText: string) => {
      originalStyles.set(el, el.getAttribute('style') || '');
      el.style.cssText += cssText;
    };

    try {
      // --- PHASE 1: DOM EXPANSION ---
      // We need to walk up from the content to the root and force them to expand
      // so html2canvas sees the full height.

      // Expand the main content
      if (scrollContainer) {
        // Force content to be fully visible (no scrollbars)
        setTempStyle(
          scrollContainer,
          'height: auto !important; overflow: visible !important; max-height: none !important;'
        );

        // Also expand the immediate child if it exists
        const childDiv = scrollContainer.querySelector(
          ':scope > div'
        ) as HTMLElement;
        if (childDiv) {
          setTempStyle(
            childDiv,
            'height: auto !important; overflow: visible !important;'
          );
        }
      }

      // Expand parents up to the previewContent to ensure no clipping occurs
      // (This handles the #dashboard height: 100% issue)
      let currentEl = scrollContainer?.parentElement;
      while (currentEl && currentEl !== previewContent.parentElement) {
        if (currentEl instanceof HTMLElement) {
          setTempStyle(
            currentEl,
            'height: auto !important; overflow: visible !important; flex: none !important;'
          );
        }
        currentEl = currentEl.parentElement;
      }

      // Handle specific elements to hide/clean up
      const tabContainer = document.getElementById('tab-container');
      if (tabContainer) {
        tabContainer.setAttribute('data-html2canvas-ignore', '');
      }

      // --- PHASE 2: CAPTURE ---
      // Apply the OKLCH sanitizer from previous steps if needed
      // (Assuming temporarilyBypassOklchStylesheets is defined outside)
      // const restoreColors = temporarilyBypassOklchStylesheets(previewContent);

      // Wait a moment for layout to settle after expansion
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(previewContent, {
        useCORS: true,
        scale: window.devicePixelRatio, // High res
        allowTaint: true,
        backgroundColor: theme === 'light' ? color._FBFCFE : color._131313,
        // CRITICAL: specific window sizing to force full render
        windowWidth: previewContent.scrollWidth,
        windowHeight: previewContent.scrollHeight,
        width: previewContent.scrollWidth,
        height: previewContent.scrollHeight,
        x: 0,
        y: 0,
        onclone: (clonedDoc) => {
          // Apply your existing modifications
          modifyImageElements(clonedDoc);

          // Ensure the cloned body also allows scrolling/expansion
          const clonedBody = clonedDoc.body;
          const clonedRoot = clonedDoc.getElementById('root');
          if (clonedBody) {
            clonedBody.style.overflow = 'visible';
          }
          if (clonedRoot) {
            clonedRoot.style.height = 'auto';
            clonedRoot.style.overflow = 'visible';
          }
        },
        logging: false,
      });

      // Restore Colors if you used the sanitizer
      // restoreColors();

      // --- PHASE 3: PROCESSING ---
      const dataUrl = canvas.toDataURL('image/png');

      if (!validateImageUrl(dataUrl)) {
        throw new Error(
          'Generated image data is invalid or potentially unsafe'
        );
      }

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to create blob'))),
          'image/png',
          1.0
        );
      });

      if (blob.size / (1024 * 1024) > 25) {
        throw new Error(
          `Image size too large (${(blob.size / (1024 * 1024)).toFixed(2)}MB)`
        );
      }

      await navigator.clipboard.write([
        new window.ClipboardItem({ 'image/png': blob }),
      ]);

      alert('Image copied successfully!');
    } catch (error) {
      console.error('Image copy error:', error);
      alert('Failed to copy image. Please try again.');
    } finally {
      // --- PHASE 4: RESTORE DOM ---
      // Revert all style changes
      originalStyles.forEach((styleStr, el) => {
        if (styleStr) {
          el.setAttribute('style', styleStr);
        } else {
          el.removeAttribute('style');
        }
      });

      // Cleanup attributes
      const tabContainer = document.getElementById('tab-container');
      if (tabContainer) {
        tabContainer.removeAttribute('data-html2canvas-ignore');
      }

      setIsDisabled(false);
      resetTooltipText();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside as unknown as EventListener
    );
    return () =>
      document.removeEventListener(
        'mousedown',
        handleClickOutside as unknown as EventListener
      );
  }, []);

  const options: Option[] = [
    {
      id: 1,
      label: 'Download PDF',
      action: () => handleExportPDF(),
    },
    {
      id: 2,
      label: 'Copy Image (PNG)',
      action: (event) => handleImageCopy(event as React.MouseEvent),
    },
  ];

  return (
    <Wrapper>
      <DownloadButton
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isDisabled}
      >
        <FiDownload size={20} />
        {isDisabled && <Tooltip>{tooltipText}</Tooltip>}
      </DownloadButton>

      {isOpen && (
        <MenuContainer ref={menuRef}>
          {options.map((option) => (
            <MenuItem
              key={option.id}
              onClick={(event: React.MouseEvent) => option.action(event)}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuContainer>
      )}
    </Wrapper>
  );
};

export default DownloadMenu;
